import { useEffect, useId, useMemo, useState } from 'react';
import type { FilterData } from './types.ts';
import {
  comparatorNames,
  filterTypes,
  filterTypesMap,
  type Comparator,
  type FilterType,
} from './config.ts';
import {
  Actions,
  Button,
  Dialog,
  Dropdown,
  Stack,
  TextField,
} from 'braid-design-system';
import { MultiselectAutosuggest } from '#src/components/MultiselectAutosuggest/MultiselectAutosuggest.tsx';
import { randomID } from '../utilities.ts';
import { useForm, type FieldErrors } from 'react-hook-form';
import type { HiringOrganizationIdFieldsFragment } from '#src/types/generated/graphql.ts';
import { HirerAutosuggest } from '#src/components/HirerAutosuggest/HirerAutosuggest.tsx';

const acceptsArray = (comparator: Comparator) =>
  ['In', 'NotIn'].includes(comparator);

const getSuggestions = (
  filterConfig: (typeof filterTypes)[number],
): Array<{ label: string; value: string }> =>
  filterConfig.values?.slice() ?? [];

type Value = string | string[] | HiringOrganizationIdFieldsFragment;

type FormValues = Omit<FilterData, 'id' | 'value'> & { value: Value };

const valueIsHirer = (
  value: Value,
): value is HiringOrganizationIdFieldsFragment =>
  typeof value === 'object' &&
  !Array.isArray(value) &&
  value.__typename === 'HiringOrganization';

const ValueField = ({
  comparator,
  type,
  register,
  value,
  setValue,
  errors,
}: {
  comparator: Comparator;
  type: FilterType;
  register: ReturnType<typeof useForm<FormValues>>['register'];
  value: Value;
  setValue: (value: Value) => void;
  errors: FieldErrors<FormValues>;
}) => {
  const field = register('value', { required: 'Required' });

  const filterConfig = filterTypesMap.get(type)!;
  const [searchText, setSearchText] = useState('');
  const valueAsText = typeof value === 'object' ? '' : value;

  const allSuggestions = useMemo(
    () =>
      getSuggestions(filterConfig).map(({ label, value: val }) => ({
        name: label,
        id: val.toString(),
        value: val,
      })),
    [filterConfig],
  );

  const suggestionLookup = useMemo(
    () =>
      new Map(allSuggestions.map((suggestion) => [suggestion.id, suggestion])),
    [allSuggestions],
  );

  const selectedItems = useMemo(
    () =>
      Array.isArray(value)
        ? value.map((item) => ({
            name:
              suggestionLookup.get(item.toString())?.name ?? item.toString(),
            id: item.toString(),
            value: item,
          }))
        : [],
    [value, suggestionLookup],
  );

  if (type === 'HirerOID') {
    const hirerValue =
      typeof value === 'object' &&
      !Array.isArray(value) &&
      value?.__typename === 'HiringOrganization'
        ? value
        : undefined;

    return (
      <HirerAutosuggest
        {...field}
        id="add-filter-value"
        onChange={(v) => {
          setSearchText(v.text);
          setValue(v.value ?? '');
        }}
        onClear={() => setSearchText('')}
        value={{ text: searchText, value: hirerValue }}
        label=""
      />
    );
  }

  if (acceptsArray(comparator)) {
    const suggestions = allSuggestions.filter((suggestion) =>
      suggestion.name.toLowerCase().includes(searchText.toLowerCase()),
    );

    return (
      <MultiselectAutosuggest
        {...field}
        id="add-filter-value"
        suggestions={suggestions}
        onItemsSelected={(items) => setValue(items.map((item) => item.value))}
        placeholder={`${filterConfig.label} values`}
        searchText={searchText}
        selectedItems={selectedItems}
        setSearchText={setSearchText}
      />
    );
  }

  if (filterConfig.values?.length) {
    return (
      <Dropdown
        {...field}
        id="add-filter-value"
        aria-label="Value"
        placeholder="Value"
        value={valueAsText}
        onChange={(e) =>
          setValue(suggestionLookup.get(e.currentTarget.value)!.value)
        }
      >
        {filterConfig.values.map(({ value: val, label }) => (
          <option key={val} value={val}>
            {label}
          </option>
        ))}
      </Dropdown>
    );
  }

  return (
    <TextField
      {...field}
      id="add-filter-value"
      aria-label="Value"
      value={valueAsText}
      placeholder={
        'placeholder' in filterConfig
          ? filterConfig.placeholder
          : `${filterConfig.label} value`
      }
      onChange={(e) => setValue(e.currentTarget.value)}
      tone={errors.value ? 'critical' : undefined}
      message={errors.value?.message}
    />
  );
};

const trimAndRemoveHiddenWhitespace = (value: string) =>
  value.trim().replace(/[\u200B-\u200D\uFEFF]/g, '');

const sanitiseValue = (value: string | string[]) =>
  Array.isArray(value)
    ? value.map((v) => trimAndRemoveHiddenWhitespace(v))
    : trimAndRemoveHiddenWhitespace(value);

export const FilterModal = ({
  onClose,
  filter,
  onAddFilter,
  onRemoveFilter,
}: {
  onClose: () => void;
  filter?: FilterData;
  onAddFilter: (filter: FilterData & { replacesId?: string }) => void;
  onRemoveFilter: (filter: FilterData) => void;
}) => {
  const {
    handleSubmit,
    register,
    watch,
    setValue: setFormValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      type: 'GraphQLOperationErrorCode',
      comparator: 'Equal',
      value: '',
    },
  });

  useEffect(() => {
    if (filter) {
      setFormValue('type', filter.type);
      setFormValue('comparator', filter.comparator);
      setFormValue(
        'value',
        Array.isArray(filter.value)
          ? filter.value.map((v) => v.toString())
          : filter.value.toString(),
      );
    }
  }, [filter, setFormValue]);

  const type = watch('type');
  const comparator = watch('comparator');
  const value = watch('value');

  const setType = (v: FilterType) => setFormValue('type', v);
  const setComparator = (v: Comparator) => setFormValue('comparator', v);
  const setValue = (v: Value) => setFormValue('value', v);

  const filterConfig = filterTypesMap.get(type)!;

  const onChangeType = (desiredType: FilterType) => {
    const newConfig = filterTypesMap.get(desiredType)!;
    let newComparator = comparator;

    if (!newConfig.comparators.includes(comparator)) {
      newComparator = newConfig.comparators[0];
      setComparator(newComparator);
    }

    if (newConfig.values) {
      if (acceptsArray(newComparator)) {
        setValue([]);
      } else {
        setValue(newConfig.values[0].value);
      }
    } else {
      setValue('');
    }

    setType(desiredType);
  };

  const onChangeComparator = (desiredComparator: Comparator) => {
    const didAcceptArray = acceptsArray(comparator);
    const willAcceptArray = acceptsArray(desiredComparator);

    if (didAcceptArray && !willAcceptArray) {
      setValue(Array.isArray(value) && value.length === 1 ? value[0] : '');
    } else if (!didAcceptArray && willAcceptArray) {
      setValue(typeof value === 'string' && value.length > 0 ? [value] : []);
    }

    setComparator(desiredComparator);
  };

  const onValid = (values: FormValues) => {
    onAddFilter({
      id: randomID(),
      replacesId: filter?.id,
      ...values,
      value: valueIsHirer(values.value)
        ? values.value.id.value
        : sanitiseValue(values.value),
    });
    onClose();
  };

  return (
    <Dialog
      id={useId()}
      title={filter ? 'Edit filter' : 'Add filter'}
      open
      onClose={onClose}
      width="small"
    >
      <form onSubmit={handleSubmit(onValid)}>
        <Stack space="small">
          <Dropdown
            {...register('type', { required: 'Required' })}
            id="add-filter-type"
            aria-label="Type"
            placeholder="Type"
            value={type}
            onChange={(e) => onChangeType(e.currentTarget.value as FilterType)}
          >
            {filterTypes.map(({ type: filterType, label }) => (
              <option key={filterType} value={filterType}>
                {label}
              </option>
            ))}
          </Dropdown>

          <Dropdown
            {...register('comparator', { required: 'Required' })}
            id="add-filter-comparator"
            aria-label="Comparison"
            placeholder="Comparison"
            value={comparator}
            onChange={(e) =>
              onChangeComparator(e.currentTarget.value as Comparator)
            }
          >
            {filterConfig.comparators.map((comp) => (
              <option key={comp} value={comp}>
                {comparatorNames[comp]}
              </option>
            ))}
          </Dropdown>

          <ValueField
            comparator={comparator}
            type={type}
            register={register}
            value={value}
            setValue={setValue}
            errors={errors}
          />

          <Actions>
            <Button tone="formAccent" type="submit">
              {filter ? 'Save filter' : 'Add filter'}
            </Button>
            {filter ? (
              <Button
                tone="critical"
                variant="ghost"
                onClick={() => {
                  onRemoveFilter(filter);
                  onClose();
                }}
              >
                Remove filter
              </Button>
            ) : null}
          </Actions>
        </Stack>
      </form>
    </Dialog>
  );
};
